import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './stylesheets/bootstrap_finetuning.css';
import './stylesheets/mobile_elements.css';
import './i18n';

import axios from 'axios';
import React from 'react';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';

import {
  clearAuthSession,
  ensureAuthorizedAccountsLoaded,
  restoreAuthSessionIfValid,
} from './actions/auth';
import AuthModal, { authModalRef } from './components/AuthModal';
import { blockedIpModalRef } from './components/BlockedIpModal';
import MainNavbar from './components/MainNavbar';
import MaintenanceBypassBanner from './components/MaintenanceBypassBanner';
import AccountModal, { accountModalRef } from './modals/AccountModal';
import BoatsModal, { boatsModalRef } from './modals/BoatsModal';
import BookingDetailsModal, {
  bookingDetailsModalRef,
} from './modals/BookingDetailsModal';
import BookingsModal, { bookingsModalRef } from './modals/BookingsModal';
import Account from './screens/Account';
import Auth from './screens/Auth';
import Badge from './screens/Badge';
import Booking from './screens/Booking';
import Bookings from './screens/Bookings';
import ConfirmBooking from './screens/ConfirmBooking';
import Contact from './screens/Contact';
import FastBookingRedirect from './screens/FastBookingRedirect';
import FindMoorings from './screens/FindMoorings';
import ForgotPassword from './screens/ForgotPassword';
import Home from './screens/Home';
import Login from './screens/Login';
import MaintainAssignedContacts from './screens/MaintainAssignedContacts';
import MaintainTimesAndPrices from './screens/MaintainTimesAndPrices';
import MobileVerification from './screens/MobileVerification';
import MooringStatus from './screens/MooringStatus';
import MooringTable from './screens/MooringTable';
import PoiRequest from './screens/PoiRequest';
import Register from './screens/Register';
import RegisterToAuthRedirect from './screens/RegisterToAuthRedirect';
import ResendEmail from './screens/ResendEmail';
import ResetPassword from './screens/ResetPassword';
import Statistics from './screens/Statistics';
import WaitingContact from './screens/WaitingContact';
import { isMobileMode } from './utils/mobileBridge';

// Routes where no login is required
const routesWithoutRequiredLogin = [
  'login',
  'auth',
  'register',
  'activate',
  'FastBooking',
  'Booking',
  'MooringStatus',
  'ResendEmail',
  'ResetPassword',
  'ConfirmBooking',
  'FindMoorings',
];
//Routes where navbar is not shown
const routesWithoutNavBar = [
  '/register/complete',
  'auth',
  'FastBooking',
  '/Booking/',
  'MooringStatus',
  'ResendEmail',
  'ResetPassword',
];

function AppRoutes() {
  const location = useLocation();
  const isForgotPasswordRoute = location.pathname.includes('ForgotPassword');
  const hideNavBar =
    routesWithoutNavBar.some((routeWithoutNavBar) =>
      location.pathname.includes(routeWithoutNavBar),
    ) ||
    (isForgotPasswordRoute && isMobileMode());

  return (
    <>
      {!hideNavBar && <MainNavbar />}
      <MaintenanceBypassBanner />
      <Switch>
        {/* <Route path='/login' component={Login} /> */}
        <Route path='/login'>
          <Redirect to='/auth' />
        </Route>
        <Route
          path='/auth/token/:mooring_transfer_token/:email?'
          component={Auth}
        />
        <Route path='/auth' component={Auth} />
        <Route path='/activate/:id' component={MobileVerification} />
        <Route
          path='/register/token/:mooring_transfer_token/:email?'
          component={Register}
        />
        <Route path='/register/complete/:account_token/' component={Register} />
        {/* Printed QR codes still use /register/&-separated args. */}
        <Route path='/register' component={RegisterToAuthRedirect} />
        <Route path='/confirm_booking/:token' component={ConfirmBooking} />
        <Route path='/account' component={Account} />
        <Route
          path={[
            '/FindMoorings/Book/:mooring_id',
            '/FindMoorings/book/:mooring_id',
          ]}
          component={FindMoorings}
        />
        <Route
          path='/FindMoorings/:marina_id?/:checkin?'
          component={FindMoorings}
        />
        {/* <Route path="/home/:id?" component={Home} /> */}
        <Route path='/home/:action?/:id?' component={Home} />
        <Route path='/MooringTable' component={MooringTable} />
        <Route
          path='/maintainTimesAndPrices/:id'
          component={MaintainTimesAndPrices}
        />
        <Route
          path='/poi-requests/reply/:token'
          render={() => <PoiRequest />}
        />
        {/* <Route path="/Contact/:id?" component={EditContact} /> */}
        <Route path='/ContactTable' component={Contact} />
        <Route path='/BadgeTable' component={Badge} />
        <Route path='/Bookings' component={Bookings} />
        <Route path='/Statistics' component={Statistics} />
        <Route path='/Renters/:id' component={MaintainAssignedContacts} />
        <Route path='/WaitingContactsTable/' component={WaitingContact} />
        {/* @deprecated FastBooking – hard-redirect old links to /FindMoorings/Book/:mooring_id */}
        <Route exact path='/FastBooking' component={FastBookingRedirect} />
        <Route
          path='/FastBooking/:mooring_id/:checkin?'
          component={FastBookingRedirect}
        />
        <Route path='/Booking/:mooring_id/:checkin?' component={Booking} />
        <Route path='/MooringStatus/:mooring_id' component={MooringStatus} />
        <Route path='/ResendEmail/:account_token' component={ResendEmail} />
        <Route path='/ForgotPassword/:email?' component={ForgotPassword} />
        <Route path='/ResetPassword/:token' component={ResetPassword} />
        <Route path='/'>
          <Redirect to='/FindMoorings' />
        </Route>
      </Switch>
    </>
  );
}

function App() {
  axios.defaults.baseURL = process.env.REACT_APP_API_HOST;
  restoreAuthSessionIfValid();
  ensureAuthorizedAccountsLoaded();

  axios.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      const status = error?.response?.status;
      // 401 means invalid login session (e.g. token from another environment)
      if (status === 401) {
        clearAuthSession();
        const currentRoute = String(window.location.pathname).split('/')[1];
        if (!routesWithoutRequiredLogin.includes(currentRoute)) {
          window.location.href = '/login';
        }
      } else if (status === 429) {
        blockedIpModalRef.current?.show();
      }
      return Promise.reject(error);
    },
  );

  return (
    <Router>
      {/* A <Switch> looks through its children <Route>s and
            renders the first one that matches the current URL. */}
      <AppRoutes />
      <AuthModal ref={authModalRef} />
      <AccountModal ref={accountModalRef} />
      <BoatsModal ref={boatsModalRef} />
      <BookingsModal ref={bookingsModalRef} />
      <BookingDetailsModal ref={bookingDetailsModalRef} />
    </Router>
  );
}

export default App;
